import { DsmProductLibraryImage } from './dsm-product-library.models';

/**
 * Which library level a step member points at. A category member re-expands to its current
 * subcategories on every read; a subcategory member is frozen — exactly that one, forever. The
 * two look identical until the next library sync, so this must always be shown, never inferred.
 */
export enum DsmScenarioMemberKind {
  Category = 1,
  Subcategory = 2
}

/** Assignment specificity. Higher is more specific and wins when two scenarios claim one scope. */
export enum DsmScenarioAssignmentTier {
  Project = 1,
  ProjectModel = 2,
  ProjectModelElevation = 3
}

/** List-card shape — matches `DsmScenarioListItemDto`. */
export interface DsmAppointmentScenarioSummary {
  id: string;
  name: string;
  /** Every scenario draws its steps' products from exactly one library. Required on create. */
  productListId: string;
  productListName: string | null;
  isActive: boolean;
  copiedFromScenarioId: string | null;
  copiedFromScenarioName: string | null;
  stepCount: number;
  assignmentCount: number;
  createdAt: string | null;
  createdByUserId: string | null;
  createdByUserName: string | null;
  lastModified: string | null;
  updatedByUserId: string | null;
  updatedByUserName: string | null;
}

/** One member of a step — matches `DsmScenarioStepMemberDto`. */
export interface DsmAppointmentStepMember {
  id: string;
  sortOrder: number;
  subGroupName: string | null;
  memberKind: DsmScenarioMemberKind;
  targetId: string;
  code: string;
  effectiveLabel: string;
  isHidden: boolean;
  isActive: boolean;
  activeProductCount: number;
  parentCategoryId: string | null;
  parentCategoryLabel: string | null;
}

/** Matches `DsmScenarioStepDto`. */
export interface DsmAppointmentStep {
  id: string;
  name: string;
  sortOrder: number;
  image: DsmProductLibraryImage | null;
  members: DsmAppointmentStepMember[];
}

/** Get-by-id shape — matches `DsmScenarioDetailDto` (the summary plus ordered steps). */
export interface DsmAppointmentScenarioDetail extends DsmAppointmentScenarioSummary {
  steps: DsmAppointmentStep[];
}

/**
 * One entry from `GET /dsm/scenarios/by-deal/{dealId}` — the same detail the get-by-id returns,
 * plus the assignment that pulled the scenario into the deal's scope. An empty list means the
 * deal has no guided appointment configured; a 404 means the deal id itself is bad.
 */
export interface DsmAppointmentDealScenario extends DsmAppointmentScenarioDetail {
  /** The assignment that brought this scenario into scope, for unassigning. */
  assignmentId: string;
  /** How specifically the scenario was assigned. Most specific wins. */
  tier: DsmScenarioAssignmentTier;
  /** Exactly one entry is `true` while the list is non-empty — the scenario the appointment uses. */
  isWinner: boolean;
}

/** Matches `DsmScenarioAssignmentDto`. */
export interface DsmAppointmentAssignment {
  id: string;
  scenarioId: string;
  scenarioName: string;
  projectId: string;
  projectName: string | null;
  modelId: string | null;
  modelName: string | null;
  elevationId: string | null;
  elevationCode: string | null;
  tier: DsmScenarioAssignmentTier;
  lotCount: number;
  /** Set when another scenario's assignment wins this exact scope. */
  shadowedByScenarioId: string | null;
  shadowedByScenarioName: string | null;
  createdAt: string | null;
}

export interface DsmAppointmentAssignableElevation {
  elevationId: string;
  elevationCode: string;
  lotCount: number;
  isAssigned: boolean;
}

export interface DsmAppointmentAssignableModel {
  modelId: string;
  modelName: string;
  elevations: DsmAppointmentAssignableElevation[];
}

export interface DsmAppointmentAssignableProject {
  projectId: string;
  projectName: string;
  models: DsmAppointmentAssignableModel[];
}

export interface CreateDsmAppointmentScenarioPayload {
  name: string;
  productListId: string;
}

export interface UpdateDsmAppointmentScenarioPayload {
  name: string;
  productListId: string;
  isActive: boolean;
}

export interface DsmAppointmentStepPayload {
  name: string;
  imageId?: string | null;
  members?: DsmAppointmentStepMemberEntry[];
}

export interface DsmAppointmentReorderPayload {
  orderedIds: string[];
}

export interface ImportDsmAppointmentStepsPayload {
  sourceScenarioId: string;
  stepIds: string[];
}

/** Exactly one of `categoryId`/`subcategoryId`; `subGroupName` may be set at the same time. */
export interface DsmAppointmentStepMemberEntry {
  categoryId?: string;
  subcategoryId?: string;
  subGroupName?: string | null;
}

export interface SetDsmAppointmentStepMembersPayload {
  members: DsmAppointmentStepMemberEntry[];
}

export interface CreateDsmAppointmentAssignmentPayload {
  projectId: string;
  modelId: string | null;
  elevationId: string | null;
}

export interface BatchDsmAppointmentAssignmentsPayload {
  contexts: CreateDsmAppointmentAssignmentPayload[];
}
